import React from 'react';
import Swal from 'sweetalert2';
import { NavigateFunction } from 'react-router-dom';

// Interface for gift creation step tracking
interface NavigationGuardOptions {
  currentGift: any; // Gift data from context
  isInGiftCreation: boolean; // Whether user is in gift creation flow
  saveGiftAsDraft: () => Promise<void>; // Save function from context
  navigate: NavigateFunction; // React Router navigate function
  targetPath: string; // Where the user wants to navigate
  activeTab?: string; // Current active tab
}

// Check if user has unsaved progress in gift creation
export const hasUnsavedGiftProgress = (currentGift: any, isInGiftCreation: boolean, activeTab?: string): boolean => {
  
  if (!isInGiftCreation || !currentGift) {
    return false;
  }
  
  // Don't consider it "unsaved progress" if user is on delivery/payment step
  // At this point, they're completing the gift, not abandoning it
  if (activeTab === '6') {
    return false;
  }
  
  // Check if user has started filling out gift data
  const hasProgress = 
    currentGift.title ||
    currentGift.recipientId ||
    currentGift.occasion ||
    currentGift.messages?.length > 0 ||
    currentGift.giftSection?.type ||
    // Add more conditions as needed for other steps
    false;
    
  return hasProgress;
};

// Global variable to prevent multiple modals
let isModalOpen = false;

// Show confirmation modal WITHOUT automatic navigation (for event-based system)
export const showNavigationConfirmationWithoutNavigation = async ({
  currentGift,
  isInGiftCreation,
  saveGiftAsDraft,
  targetPath,
  activeTab
}: Omit<NavigationGuardOptions, 'navigate'>): Promise<boolean> => {
  
  
  // Prevent multiple modals from opening
  if (isModalOpen) {
    return false;
  }
  
  // If no unsaved progress, allow navigation
  if (!hasUnsavedGiftProgress(currentGift, isInGiftCreation, activeTab)) {
    return true;
  }

  
  // Mark modal as open
  isModalOpen = true;
  
  try {
    // Show SweetAlert confirmation modal
    const result = await Swal.fire({
      title: 'Save Your Progress?',
      text: 'You have unsaved changes in your gift creation. What would you like to do?',
      icon: 'question',
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: 'Save to Draft',
      denyButtonText: 'Keep Editing',
      cancelButtonText: 'Discard & Leave',
      confirmButtonColor: '#3957A0',
      denyButtonColor: '#6c757d',
      cancelButtonColor: '#dc3545',
      allowOutsideClick: false,
      allowEscapeKey: false,
      customClass: {
        popup: 'custom-swal-popup',
        title: 'custom-swal-title',
        htmlContainer: 'custom-swal-text'
      }
    });


    if (result.isConfirmed) {
      // User chose "Save to Draft"
      try {
        await saveGiftAsDraft();
        
        // Emit navigation approval event
        window.dispatchEvent(new CustomEvent('navigationApproved'));
        
        return true; // Allow navigation after saving
      } catch (error) {
        Swal.fire({
          title: 'Error',
          text: 'Failed to save draft. Please try again.',
          icon: 'error',
          confirmButtonColor: '#3957A0'
        });
        return false; // Block navigation on save error
      }
    } else if (result.isDenied) {
      // User chose "Keep Editing" - stay on current page
      return false; // Block navigation
    } else if (result.dismiss === Swal.DismissReason.cancel) {
      // User chose "Discard & Leave"
      
      // Emit navigation approval event
      window.dispatchEvent(new CustomEvent('navigationApproved'));
      
      return true; // Allow navigation without saving
    }

    return false; // Block navigation by default
  } catch (error) {
    return false; // Block navigation on error
  } finally {
    // Always reset the modal flag
    isModalOpen = false;
  }
};

// Show confirmation modal and handle navigation (original function for backward compatibility)
export const showNavigationConfirmation = async ({
  currentGift,
  isInGiftCreation,
  saveGiftAsDraft,
  navigate,
  targetPath,
  activeTab
}: NavigationGuardOptions): Promise<boolean> => {
  
  
  // If no unsaved progress, allow navigation
  if (!hasUnsavedGiftProgress(currentGift, isInGiftCreation, activeTab)) {
    navigate(targetPath);
    return true;
  }

  // Prevent multiple modals in this function too
  if (isModalOpen) {
    return false;
  }
  
  
  isModalOpen = true;
  
  try {
    // Show SweetAlert confirmation modal
    const result = await Swal.fire({
      title: 'Save Your Progress?',
      text: 'You have unsaved changes in your gift creation. What would you like to do?',
      icon: 'question',
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: 'Save to Draft',
      denyButtonText: 'Keep Editing',
      cancelButtonText: 'Discard & Leave',
      confirmButtonColor: '#3957A0',
      denyButtonColor: '#6c757d',
      cancelButtonColor: '#dc3545',
      allowOutsideClick: false,
      allowEscapeKey: false,
      customClass: {
        popup: 'custom-swal-popup',
        title: 'custom-swal-title',
        htmlContainer: 'custom-swal-text'
      }
    });


    if (result.isConfirmed) {
      // User chose "Save to Draft"
      try {
        await saveGiftAsDraft();
        
        // Emit navigation approval event before navigating
        window.dispatchEvent(new CustomEvent('navigationApproved'));
        
        navigate(targetPath);
        return true;
      } catch (error) {
        Swal.fire({
          title: 'Error',
          text: 'Failed to save draft. Please try again.',
          icon: 'error',
          confirmButtonColor: '#3957A0'
        });
        return false;
      }
    } else if (result.isDenied) {
      // User chose "Keep Editing" - stay on current page
      return false;
    } else if (result.dismiss === Swal.DismissReason.cancel) {
      // User chose "Discard & Leave"
      
      // Emit navigation approval event before navigating
      window.dispatchEvent(new CustomEvent('navigationApproved'));
      
      navigate(targetPath);
      return true;
    }

    return false;
  } catch (error) {
    return false;
  } finally {
    // Always reset the modal flag
    isModalOpen = false;
  }
};

// Utility function to determine if current route is gift creation
export const isGiftCreationRoute = (pathname: string): boolean => {
  return pathname.includes('/start-gift') || 
         pathname.includes('/my-gifts/create/') ||
         pathname.includes('/my-gifts/edit/') ||
         pathname.includes('/gift/') ||
         pathname.includes('/occasion') ||
         pathname.includes('/message') ||
         pathname.includes('/delivery') ||
         pathname.includes('/review');
};

// Hook for preventing browser back/forward navigation during gift creation
export const useBeforeUnload = (isInGiftCreation: boolean, hasUnsavedProgress: boolean) => {
  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isInGiftCreation && hasUnsavedProgress) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isInGiftCreation, hasUnsavedProgress]);
};